import * as React from "react";
import { usePathname, useNavigate as useRouter, useSearch as useSearchParams } from "../../router";

import { Button } from "../../button";
import { DataTable, type DataTableColumn } from "../data-table";
import { IconTooltip } from "../icon-tooltip";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../select";
import { Skeleton } from "../../skeleton";
import { StatusBadge, IntentBadge } from "../status-badge";
import { TablePager } from "../table-pager";
import { classifyRunExit } from "../../tokens/run-exit";
import { formatDateTime, formatNumber, formatUsd } from "../../format";
import type { WorkerRunStatus } from "../../tokens/status";
import { useApi } from "../../api/client";
import { usePagedList } from "../../api/use-paged-list";
import { ArrowDown, ArrowUp, ChevronsUpDown, X } from "../../icons";
import {
  workflowRunsList,
  workflowRunFailures,
  type WorkflowRunRowDto,
  type WorkflowRunsQuery,
} from "../../api/contracts/workflow-runs";
import { SectionCard } from "../section-card";

/**
 * The workflow's runs, LIVE — one implementation shared by the product page
 * (`/agents/workflows/[slug]`) and the design prototype, which used to carry
 * forked copies that drifted (the parity audit's §6: the product's copy had lost
 * the outcome verdicts, the date filters, right-aligned numerics, keyboard nav
 * and the exit-reason diagnosis).
 *
 * Every knob is a URL param, so the table is a bookmarkable, back-button-able
 * view: filters (`role`/`status`/`outcome`/`dayFrom`/`dayTo`), sort
 * (`sort`/`dir`) and page (`page`/`page_size`) all live in the query string, and
 * `usePagedList` re-reads them → the server re-queries `excellent_worker_runs`
 * (loop-scoped) → the table re-renders. The Activity chart above writes the SAME
 * `dayFrom`/`dayTo` on a day click, so clicking a day filters this table and
 * editing the table's date inputs moves the chart's mark — one source of truth,
 * the URL.
 *
 * ⚠️ The URL keys are `dayFrom`/`dayTo`, NOT `from`/`to` (#44400) — `from` is
 * shell-wide (`NAV_FROM_PARAM`, back-link provenance read by the mode rail and
 * the breadcrumb on every route). A day click used to write `?from=2026-07-29`
 * and the chrome then read that date as "the place you came from". The
 * `workflowRunsQuery` CONTRACT fields below are still `from`/`to` (server-side,
 * unambiguous) — only the URL param NAMES changed; the values they carry are
 * unchanged (inclusive `YYYY-MM-DD` day bounds).
 *
 * Sorting is SERVER-side (the whole result set, not just the visible page): a
 * header writes `?sort=&dir=`, never a client comparator on `DataTable`, which
 * would only reorder the current 25 rows.
 */

type SortKey =
  | "id"
  | "started"
  | "role"
  | "status"
  | "verified"
  | "cost"
  | "duration"
  | "model";

const SORT_KEYS: readonly SortKey[] = [
  "id",
  "started",
  "role",
  "status",
  "verified",
  "cost",
  "duration",
  "model",
];

/** Columns that read newest / biggest-first on their first click (mirror of the
 *  server `descByDefault`). Text columns default ascending. */
const DESC_FIRST = new Set<SortKey>(["id", "started", "verified", "cost", "duration"]);

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "running", label: "Running" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "budget_exceeded", label: "Budget exceeded" },
  { value: "aborted", label: "Aborted" },
];
const OUTCOME_OPTIONS = [
  { value: "", label: "Any outcome" },
  { value: "pass", label: "Passed" },
  { value: "fail", label: "Failed verdict" },
];

/** Roles a workflow run can be attributed to. `loop-runner` is the runner's own
 *  attribution for a pass that isn't a named role — it appears in the ledger, so
 *  it appears here; a filter that can't name a value in the data is a dead end. */
const DEFAULT_ROLE_OPTIONS = [
  { value: "", label: "All roles" },
  { value: "shipper", label: "Shipper" },
  { value: "verifier", label: "Verifier" },
  { value: "loop-runner", label: "Loop runner" },
];

function fmtDur(ms: number | null | undefined): string {
  if (ms == null) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return (s % 60 ? `${m}m ${s % 60}s` : `${m}m`);
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** The runs table wrapped in its own card — the default (unlocked) Runs view. */
export function WorkflowRunsTable({
  loopId,
  roleOptions = DEFAULT_ROLE_OPTIONS,
}: {
  loopId: number;
  roleOptions?: { value: string; label: string }[];
}) {
  return (
    <SectionCard title="Runs">
      <RunsTableBody loopId={loopId} roleOptions={roleOptions} />
    </SectionCard>
  );
}

/** The pipeline drill-in: a step's non-completing runs, live. The bucket summary
 *  is the true ledger rollup (`workflowRunFailures`), and the table below is the
 *  same body locked to `role` + failed-only. Reached from a step's failure strip
 *  on the Pipeline tab. */
export function WorkflowFailedRunsCard({
  loopId,
  role,
  stepName,
  onClear,
}: {
  loopId: number;
  role: string;
  stepName: string;
  onClear: () => void;
}) {
  const { data } = useApi(workflowRunFailures, { query: { loop_id: loopId, role } });
  const total = data?.total ?? 0;
  // Roll the raw exit_reasons up through the shipped buckets, heaviest first — so
  // this line and /agents/analytics can never disagree about the same runs.
  const byBucket = new Map<string, { label: string; count: number }>();
  for (const f of data?.rows ?? []) {
    const b = classifyRunExit(f.exit_reason);
    const cur = byBucket.get(b.key);
    byBucket.set(b.key, { label: b.label, count: (cur?.count ?? 0) + f.count });
  }
  const buckets = [...byBucket.values()].sort((a, b) => b.count - a.count);

  return (
    <SectionCard
      title={`Didn't finish · ${cap(stepName)}`}
      aside={
        <span className="type-subtext tabular-nums text-muted-foreground">
          {formatNumber(total)} runs
        </span>
      }
      action={
        <Button variant="ghost-muted" size="nav" className="-mr-2" onClick={onClear}>
          Show all
          <X />
        </Button>
      }
    >
      {buckets.length > 0 && (
        <p className="type-body text-muted-foreground">
          {buckets.map((b, i) => (
            <React.Fragment key={b.label}>
              {i > 0 && " · "}
              <span className="tabular-nums text-foreground">{formatNumber(b.count)}</span>{" "}
              {b.label}
            </React.Fragment>
          ))}
        </p>
      )}
      <RunsTableBody loopId={loopId} lock={{ role, failedOnly: true }} />
    </SectionCard>
  );
}

/** The table + pager (+ filter bar when unlocked). `lock` fixes the scope for the
 *  pipeline drill-in — a role + the failed-only ending — and hides the filter bar;
 *  sort and page stay URL-driven either way. */
function RunsTableBody({
  loopId,
  lock,
  roleOptions = DEFAULT_ROLE_OPTIONS,
}: {
  loopId: number;
  lock?: { role?: string; failedOnly?: boolean };
  roleOptions?: { value: string; label: string }[];
}) {
  const search = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const locked = lock != null;
  // Locked (drill-in): the scope is the role + failed-only the step handed us; the
  // URL filter params are ignored (no filter bar is shown). Unlocked: everything
  // comes from the URL.
  const role = locked ? lock.role ?? "" : search.get("role") ?? "";
  const status = locked ? "" : search.get("status") ?? "";
  const outcome = locked ? "" : search.get("outcome") ?? "";
  // Local var names stay `from`/`to` (they feed the CONTRACT'S `from`/`to`
  // query fields below unchanged) — only the URL key differs (#44400).
  const from = locked ? "" : search.get("dayFrom") ?? "";
  const to = locked ? "" : search.get("dayTo") ?? "";
  const failedOnly = locked ? Boolean(lock.failedOnly) : false;
  // Guard the sort against the whitelist: a stray `?sort=` from a hand-edited URL
  // degrades to the default order rather than 400-ing the whole table (the
  // contract's enum would reject it).
  const rawSort = search.get("sort") ?? "";
  const sort: SortKey | "" = (SORT_KEYS as readonly string[]).includes(rawSort)
    ? (rawSort as SortKey)
    : "";
  const dir = search.get("dir") === "asc" ? "asc" : "desc";
  const anyFilter = Boolean(role || status || outcome || from || to);

  /** Write a set of params, dropping empties; reset to page 1 unless told not to. */
  const patch = React.useCallback(
    (changes: Record<string, string | null>, resetPage = true) => {
      const next = new URLSearchParams(search.toString());
      for (const [k, v] of Object.entries(changes)) {
        if (v == null || v === "") next.delete(k);
        else next.set(k, v);
      }
      if (resetPage) next.delete("page");
      const qs = next.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    },
    [search, pathname, router],
  );

  function onSort(col: SortKey) {
    if (sort === col) {
      // Same column → flip direction; keep the page (re-reading, not re-filtering).
      patch({ sort: col, dir: dir === "desc" ? "asc" : "desc" }, false);
    } else {
      patch({ sort: col, dir: DESC_FIRST.has(col) ? "desc" : "asc" }, false);
    }
  }

  // Only the SET filters ride the query — an empty select adds nothing, so the
  // SWR key (and the URL) stay clean.
  const query = {
    loop_id: loopId,
    ...(role ? { role } : {}),
    ...(failedOnly ? { failed_only: true } : {}),
    ...(status ? { status } : {}),
    ...(outcome ? { outcome } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...(sort ? { sort, dir } : {}),
  } as Omit<WorkflowRunsQuery, "page" | "page_size">;

  const { rows, isLoading, pager } = usePagedList(workflowRunsList, {
    query,
    defaultPageSize: 25,
    // Hold the previous page while a new filter/sort/page revalidates, so the
    // table doesn't flash empty on every click.
    swr: { keepPreviousData: true },
  });

  const columns: DataTableColumn<WorkflowRunRowDto>[] = [
    {
      key: "id",
      header: (
        <SortHeader col="id" sort={sort} dir={dir} onSort={onSort}>
          Run
        </SortHeader>
      ),
      width: "w-16",
      render: (r) => <span className="font-mono type-subtext text-muted-foreground">#{r.id}</span>,
    },
    {
      key: "started",
      header: (
        <SortHeader col="started" sort={sort} dir={dir} onSort={onSort}>
          Started
        </SortHeader>
      ),
      render: (r) => (
        <span className="whitespace-nowrap tabular-nums text-muted-foreground">
          {formatDateTime(r.started_at)}
        </span>
      ),
    },
    {
      key: "role",
      header: (
        <SortHeader col="role" sort={sort} dir={dir} onSort={onSort}>
          Role
        </SortHeader>
      ),
      render: (r) => <span className="capitalize">{r.role}</span>,
    },
    {
      key: "status",
      header: (
        <SortHeader col="status" sort={sort} dir={dir} onSort={onSort}>
          Status
        </SortHeader>
      ),
      render: (r) => <StatusCell run={r} />,
    },
    {
      key: "verified",
      header: (
        <SortHeader col="verified" sort={sort} dir={dir} onSort={onSort}>
          Verified
        </SortHeader>
      ),
      render: (r) => <span className="tabular-nums">{r.tasks_verified || "—"}</span>,
    },
    {
      key: "cost",
      header: (
        <SortHeader col="cost" sort={sort} dir={dir} onSort={onSort}>
          Cost
        </SortHeader>
      ),
      render: (r) => (
        <span className="tabular-nums">{r.cost_usd == null ? "—" : formatUsd(r.cost_usd)}</span>
      ),
    },
    {
      key: "duration",
      header: (
        <SortHeader col="duration" sort={sort} dir={dir} onSort={onSort}>
          Duration
        </SortHeader>
      ),
      render: (r) => (
        <span className="tabular-nums text-muted-foreground">{fmtDur(r.wall_ms)}</span>
      ),
    },
    {
      key: "model",
      header: (
        <SortHeader col="model" sort={sort} dir={dir} onSort={onSort}>
          Model
        </SortHeader>
      ),
      render: (r) => (
        <span className="font-mono type-subtext text-muted-foreground">{r.model ?? "—"}</span>
      ),
    },
  ];

  return (
    <>
      {!locked && (
        <RunsFilterBar
          role={role}
          status={status}
          outcome={outcome}
          from={from}
          to={to}
          anyFilter={anyFilter}
          roleOptions={roleOptions}
          onChange={patch}
        />
      )}

      {isLoading && rows.length === 0 ? (
        <div className="flex flex-col gap-2 py-2">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : (
        <div className="-mx-6">
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(r) => r.id}
            rowHref={(r) => `/agents/runs/${r.id}`}
            keyboardNav
            wrapInCard={false}
            empty={locked ? "No runs for this step in the ledger." : "No runs match these filters."}
          />
        </div>
      )}

      <TablePager {...pager} defaultPageSize={25} unitLabel="runs" />
    </>
  );
}

/** A clickable, URL-writing column header. Server-side sort: the arrow reflects
 *  `?sort=&dir=`, the click rewrites them. */
function SortHeader({
  col,
  sort,
  dir,
  onSort,
  children,
}: {
  col: SortKey;
  sort: SortKey | "";
  dir: "asc" | "desc";
  onSort: (col: SortKey) => void;
  children: React.ReactNode;
}) {
  const active = sort === col;
  return (
    <button
      type="button"
      onClick={() => onSort(col)}
      aria-label={`Sort by ${typeof children === "string" ? children : col}`}
      className="group -mx-1 inline-flex items-center gap-1 rounded px-1 py-0.5 text-inherit transition-colors hover:text-foreground"
    >
      <span>{children}</span>
      {active ? (
        dir === "desc" ? (
          <ArrowDown className="size-3" />
        ) : (
          <ArrowUp className="size-3" />
        )
      ) : (
        <ChevronsUpDown className="size-3 opacity-0 transition-opacity group-hover:opacity-40" />
      )}
    </button>
  );
}

/** Status as the page reads it: running → info, a pass/fail verdict → its intent,
 *  otherwise the worker-run status badge with the raw exit reason on hover. The
 *  tooltip carries the DIAGNOSIS (bucket hint + the verbatim `exit_reason`) —
 *  repeating the badge's own word would answer nothing. */
function StatusCell({ run }: { run: WorkflowRunRowDto }) {
  if (run.status === "running") return <IntentBadge intent="info">running</IntentBadge>;
  if (run.outcome === "pass") return <IntentBadge intent="success">pass</IntentBadge>;
  if (run.outcome === "fail") return <IntentBadge intent="danger">fail</IntentBadge>;
  const badge = <StatusBadge domain="worker_run" status={run.status as WorkerRunStatus} />;
  if (!run.exit_reason) return badge;
  const bucket = classifyRunExit(run.exit_reason);
  return (
    <IconTooltip
      label={
        <span className="flex max-w-72 flex-col gap-1">
          <span>{bucket.hint}</span>
          <span className="font-mono type-subtext break-all opacity-70">{run.exit_reason}</span>
        </span>
      }
    >
      <span className="cursor-default border-b border-dotted border-border">{badge}</span>
    </IconTooltip>
  );
}

function RunsFilterBar({
  role,
  status,
  outcome,
  from,
  to,
  anyFilter,
  roleOptions,
  onChange,
}: {
  role: string;
  status: string;
  outcome: string;
  from: string;
  to: string;
  anyFilter: boolean;
  roleOptions: { value: string; label: string }[];
  onChange: (changes: Record<string, string | null>) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <FilterSelect
        label="Role"
        value={role}
        options={roleOptions}
        onValueChange={(v) => onChange({ role: v })}
      />
      <FilterSelect
        label="Status"
        value={status}
        options={STATUS_OPTIONS}
        onValueChange={(v) => onChange({ status: v })}
      />
      <FilterSelect
        label="Outcome"
        value={outcome}
        options={OUTCOME_OPTIONS}
        onValueChange={(v) => onChange({ outcome: v })}
      />
      <div className="flex items-center gap-1.5 type-subtext text-muted-foreground">
        <span>From</span>
        <DateInput
          value={from}
          max={to || undefined}
          label="From date"
          onChange={(v) => onChange({ dayFrom: v })}
        />
        <span>to</span>
        <DateInput
          value={to}
          min={from || undefined}
          label="To date"
          onChange={(v) => onChange({ dayTo: v })}
        />
      </div>
      {anyFilter && (
        <Button
          variant="ghost-muted"
          size="nav"
          onClick={() =>
            onChange({ role: null, status: null, outcome: null, dayFrom: null, dayTo: null })
          }
        >
          <X />
          Clear
        </Button>
      )}
    </div>
  );
}

/** A labelled filter dropdown. Radix forbids an empty item value, so "All" maps
 *  to the `__all` sentinel in the control and back to `""` (param removed) out. */
function FilterSelect({
  label,
  value,
  options,
  onValueChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onValueChange: (value: string) => void;
}) {
  return (
    <Select value={value || "__all"} onValueChange={(v) => onValueChange(v === "__all" ? "" : v)}>
      {/* Width only — the h-7 height is `size="default"`'s job, not a repaint. */}
      <SelectTrigger className="w-auto min-w-[8.5rem]" aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value || "__all"} value={o.value || "__all"}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function DateInput({
  value,
  min,
  max,
  label,
  onChange,
}: {
  value: string;
  min?: string;
  max?: string;
  label: string;
  onChange: (value: string) => void;
}) {
  return (
    <input
      type="date"
      value={value}
      min={min}
      max={max}
      aria-label={label}
      onChange={(e) => onChange(e.target.value)}
      className="h-7 rounded-md border border-border bg-transparent px-2 type-subtext tabular-nums text-foreground [color-scheme:light_dark]"
    />
  );
}
